import { useEffect, useState } from 'react';

import {
  getAdmissionSummary, updateAdmissionSettings, MODE_LABEL,
} from '../../lib/admissionsAdmin';
import type { AdmissionMode } from '../../lib/admissions';
import { ApiError } from '../../lib/apiClient';

const SELECTABLE: AdmissionMode[] = ['ticketed', 'rsvp', 'request', 'invite_only'];

/**
 * How this event will admit people, chosen while it is being created.
 *
 * ── THE DEFAULT IS WHAT VOLETO ALREADY DID ───────────────────────────────────────────
 *
 * §88. A new event is `ticketed` and this control does not have to be touched, so the
 * existing creation flow is unchanged for anybody who does not care. Choosing something else
 * is deliberate, and it swaps the tier editor for the handful of settings that mode needs
 * rather than showing both.
 *
 * ── AND IT IS NOT A NEW STEP ─────────────────────────────────────────────────────────
 *
 * This sits inside the existing tickets step, which it renames rather than replaces. A
 * fifth step would have renumbered the whole wizard and given a ticketed event a screen it
 * has no use for.
 */
export function WizardAdmissionStep({
  eventId, onModeChange,
}: { eventId: string; onModeChange: (mode: AdmissionMode) => void }) {
  const [mode, setMode] = useState<AdmissionMode>('ticketed');
  const [capacity, setCapacity] = useState('');
  const [partyMax, setPartyMax] = useState(1);
  const [waitlist, setWaitlist] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getAdmissionSummary(eventId)
      .then((s) => {
        setMode(s.settings.mode);
        setCapacity(s.settings.admissionCapacity == null ? '' : String(s.settings.admissionCapacity));
        setPartyMax(s.settings.partySizeMax);
        setWaitlist(s.settings.waitlistEnabled);
        onModeChange(s.settings.mode);
      })
      .catch(() => { /* A brand-new event has no settings row, which means ticketed. */ });
  }, [eventId, onModeChange]);

  const usesCapacity = mode !== 'ticketed';

  async function save(next: AdmissionMode) {
    setBusy(true);
    setError(null);
    try {
      await updateAdmissionSettings(eventId, {
        mode: next,
        admissionCapacity: capacity === '' ? null : Number(capacity),
        partySizeMin: 1,
        partySizeMax: Number(partyMax),
        waitlistEnabled: next === 'ticketed' ? false : waitlist,
      });
      setMode(next);
      onModeChange(next);
      setSaved(true);
    } catch (err) {
      const payload = err instanceof ApiError
        ? (err.payload as { message?: string } | null) : null;
      setError(payload?.message ?? 'That could not be saved.');
    } finally { setBusy(false); }
  }

  return (
    <div className="wiz-admission">
      <label className="field">
        <span className="field__label">How do people get in?</span>
        <select
          className="field__input"
          value={mode}
          disabled={busy}
          onChange={(e) => { setSaved(false); void save(e.target.value as AdmissionMode); }}
        >
          {SELECTABLE.map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
        </select>
        <span className="field__help">
          {mode === 'ticketed' && 'They buy a ticket. Set the tiers and prices below.'}
          {mode === 'rsvp' && 'They confirm immediately, with no payment, while there is room.'}
          {mode === 'request' && 'They ask, and somebody approves or declines. No ticket until approved.'}
          {mode === 'invite_only' && 'Only somebody holding a valid invitation can get in.'}
        </span>
      </label>

      {error && <p className="alert alert--error" role="alert">{error}</p>}
      {saved && !error && <p className="org-flash">Saved.</p>}

      {usesCapacity && (
        <>
          <div className="wiz-grid">
            <label className="field">
              <span className="field__label">How many people can come in total?</span>
              <input className="field__input" type="number" min={0} value={capacity}
                placeholder="No limit" disabled={busy}
                onChange={(e) => { setCapacity(e.target.value); setSaved(false); }}
                onBlur={() => { void save(mode); }} />
            </label>
            <label className="field">
              <span className="field__label">Largest party</span>
              <input className="field__input" type="number" min={1} value={partyMax}
                disabled={busy}
                onChange={(e) => { setPartyMax(Number(e.target.value)); setSaved(false); }}
                onBlur={() => { void save(mode); }} />
            </label>
          </div>

          <label className="field field--check">
            <input type="checkbox" checked={waitlist} disabled={busy}
              onChange={(e) => { setWaitlist(e.target.checked); void save(mode); }} />
            <span className="field__label">Offer a waitlist when it fills up</span>
          </label>

          <p className="org-hint">
            There are no ticket tiers on this kind of event: admission is confirmed without a
            sale, and everything else — the QR, the ticket page, the door scanner — works
            exactly as it does for a purchased ticket.
          </p>
        </>
      )}
    </div>
  );
}
